import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { NoBrandSelected } from '@/components/settings/BrandScopedTab';

interface RequireBrandContextProps {
  children: ReactNode;
  /** When set, brand-less agency staff are redirected here (replace) instead of seeing the empty state. */
  redirectTo?: string;
}

/**
 * Route-level guard for brand-scoped pages (creator, media library, calendar, analytics, ...).
 *
 * Agency staff without a home team who are not in God Mode have no brand (team) context, so
 * every team-scoped request would fail with "No team context". For them we render the shared
 * "Choose a brand first" empty state inside the normal layout instead of the page.
 *
 * Everyone else renders the page unchanged, including non-staff users without a team
 * (their behaviour, e.g. the no_plan -> /shop redirect in PrivateRoute, is untouched).
 */
export const RequireBrandContext = ({ children, redirectTo }: RequireBrandContextProps) => {
  const { hasTeam, isAgencyStaff } = useEffectiveTeamId();

  if (isAgencyStaff && !hasTeam) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return (
      <div className="p-6">
        <NoBrandSelected />
      </div>
    );
  }

  return <>{children}</>;
};
